import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
    ArrowLeft, Save, CheckCircle2, Play, AlertTriangle, HandMetal,
    Workflow, GitBranch, ShieldCheck, StopCircle, Zap, Users, Trash2, Plus,
    Sparkles, Loader2,
} from "lucide-react";
import ReactFlow, {
    Background, Controls, MiniMap, addEdge, applyEdgeChanges, applyNodeChanges,
    Handle, Position, MarkerType,
} from "reactflow";
import "reactflow/dist/style.css";
import api from "@/lib/api";
import { toast } from "sonner";
import ConfirmDeleteModal from "@/components/ConfirmDeleteModal";

/* ================================================================
   Nexus One · Orchestration Builder Canvas — Phase 5-B / D2
   ================================================================ */

const PALETTE = [
    { type: "trigger",          label: "Trigger",          icon: Zap,          color: "#8b5cf6" },
    { type: "workforce_action", label: "Workforce Action", icon: Users,        color: "#22d3ee" },
    { type: "condition",        label: "Condition",        icon: GitBranch,    color: "#fbbf24" },
    { type: "approval_gate",    label: "Approval Gate",    icon: ShieldCheck,  color: "#f472b6" },
    { type: "end",              label: "End",              icon: StopCircle,   color: "#34d399" },
];

const NODE_META = Object.fromEntries(PALETTE.map((p) => [p.type, p]));

function AtlasNode({ id, data, selected }) {
    const meta = NODE_META[data.type] || NODE_META.trigger;
    const Icon = meta.icon;
    const isCond = data.type === "condition";
    return (
        <div
            data-testid={`orch-node-instance-${id}`}
            className="rounded-xl px-3 py-2.5 min-w-[190px] transition-all"
            style={{
                background: selected
                    ? "linear-gradient(180deg, rgba(139,92,246,0.16) 0%, rgba(10,10,26,0.92) 100%)"
                    : "linear-gradient(180deg, rgba(23,23,42,0.92) 0%, rgba(10,10,26,0.92) 100%)",
                border: `1px solid ${selected ? meta.color : "var(--atlas-border-strong)"}`,
                boxShadow: selected
                    ? `0 0 0 3px ${meta.color}22, 0 8px 22px -10px ${meta.color}55`
                    : "0 4px 12px -4px rgba(0,0,0,0.35)",
            }}
        >
            <Handle type="target" position={Position.Left}
                style={{ background: meta.color, width: 8, height: 8, border: "none" }} />
            <div className="flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5" strokeWidth={2.25} style={{ color: meta.color }} />
                <div className="text-[10.5px] font-semibold uppercase tracking-widest"
                    style={{ color: meta.color }}>
                    {meta.label}
                </div>
            </div>
            <div className="mt-1 text-[12.5px] font-medium truncate"
                style={{ color: "var(--atlas-text-primary)" }}>
                {(data.config && (data.config.label || data.config.alias)) || data.type}
            </div>
            {data.type === "workforce_action" && (data.config?.worker_role || data.config?.worker_slug) && (
                <div className="mt-0.5 text-[10.5px] truncate"
                    style={{ color: "var(--atlas-text-tertiary)" }}>
                    {data.config?.worker_role || data.config?.worker_slug}
                </div>
            )}
            {data.type === "condition" && data.config?.expression && (
                <div className="mt-0.5 text-[10.5px] truncate font-mono"
                    style={{ color: "var(--atlas-text-muted)" }}>
                    {data.config.expression}
                </div>
            )}
            {data.type === "approval_gate" && data.config?.approver_role && (
                <div className="mt-0.5 text-[10.5px] truncate"
                    style={{ color: "var(--atlas-text-tertiary)" }}>
                    → {data.config.approver_role}
                </div>
            )}
            {isCond ? (
                <>
                    <Handle type="source" position={Position.Right} id="true"
                        style={{ background: "#34d399", top: "40%", width: 8, height: 8, border: "none" }} />
                    <Handle type="source" position={Position.Right} id="false"
                        style={{ background: "#f87171", top: "70%", width: 8, height: 8, border: "none" }} />
                </>
            ) : (
                <Handle type="source" position={Position.Right}
                    style={{ background: meta.color, width: 8, height: 8, border: "none" }} />
            )}
        </div>
    );
}

const NODE_TYPES = { atlas: AtlasNode };

const rfNodesFromOrch = (nodes) => (nodes || []).map((n) => ({
    id: n.id,
    type: "atlas",
    position: n.position || { x: 0, y: 0 },
    data: { type: n.type, config: n.config || {} },
}));

const rfEdgesFromOrch = (edges) => (edges || []).map((e) => ({
    id: e.id,
    source: e.source_node_id,
    target: e.target_node_id,
    sourceHandle: e.branch || null,
    markerEnd: { type: MarkerType.ArrowClosed, color: e.branch === "false" ? "#f87171" : (e.branch === "true" ? "#34d399" : "#8b5cf6") },
    style: {
        stroke: e.branch === "false" ? "#f87171" : (e.branch === "true" ? "#34d399" : "#8b5cf6"),
        strokeWidth: 2,
    },
    label: e.branch === "true" ? "true" : e.branch === "false" ? "false" : "",
    labelStyle: { fill: "#cbd5e1", fontSize: 10, fontWeight: 600 },
    labelBgStyle: { fill: "#0a0a1a" },
}));

const orchNodesFromRf = (rfNodes) => rfNodes.map((n) => ({
    id: n.id, type: n.data.type, position: n.position, config: n.data.config || {},
}));

const orchEdgesFromRf = (rfEdges) => rfEdges.map((e) => ({
    id: e.id,
    source_node_id: e.source,
    target_node_id: e.target,
    branch: e.sourceHandle || null,
    condition: e.sourceHandle ? { expr: "", when_true: e.sourceHandle === "true" } : null,
}));

export default function OrchestrationBuilder() {
    const { orchId } = useParams();
    const nav = useNavigate();

    const [name, setName] = useState("Untitled orchestration");
    const [description, setDescription] = useState("");
    const [triggerType, setTriggerType] = useState("manual");
    const [status, setStatus] = useState("draft");
    const [rfNodes, setRfNodes] = useState([]);
    const [rfEdges, setRfEdges] = useState([]);
    const [selected, setSelected] = useState(null);
    const [errors, setErrors] = useState([]);
    const [saving, setSaving] = useState(false);
    const [activating, setActivating] = useState(false);
    const [validating, setValidating] = useState(false);
    const [running, setRunning] = useState(false);
    const [deleteOpen, setDeleteOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [hired, setHired] = useState([]);
    const [dirty, setDirty] = useState(false);
    const [currentId, setCurrentId] = useState(orchId || null);

    // -------- Load existing orch or bootstrap fresh --------
    useEffect(() => {
        (async () => {
            try {
                const { data: hr } = await api.get("/workers/hired");
                setHired(hr.hired || []);
            } catch {}

            if (orchId) {
                try {
                    const { data } = await api.get(`/orchestrations/${orchId}`);
                    setName(data.name || "Untitled");
                    setDescription(data.description || "");
                    setTriggerType(data.trigger?.type || "manual");
                    setStatus(data.status || "draft");
                    setRfNodes(rfNodesFromOrch(data.nodes));
                    setRfEdges(rfEdgesFromOrch(data.edges));
                    setCurrentId(orchId);
                    setDirty(false);
                } catch (e) {
                    toast.error("Could not load orchestration.");
                }
            } else {
                // Fresh: seed with a trigger + end so the canvas isn't empty
                const trigId = "trig_" + Math.random().toString(36).slice(2, 8);
                const endId  = "end_"  + Math.random().toString(36).slice(2, 8);
                setRfNodes([
                    { id: trigId, type: "atlas", position: { x: 40, y: 160 }, data: { type: "trigger", config: { label: "Manual" } } },
                    { id: endId,  type: "atlas", position: { x: 460, y: 160 }, data: { type: "end", config: { marker: "success", summary: "Done" } } },
                ]);
                setRfEdges([]);
                setDirty(true);
            }
        })();
    // eslint-disable-next-line
    }, [orchId]);

    // -------- Canvas events --------
    const onNodesChange = useCallback((changes) => {
        setRfNodes((n) => applyNodeChanges(changes, n));
        setDirty(true);
    }, []);
    const onEdgesChange = useCallback((changes) => {
        setRfEdges((e) => applyEdgeChanges(changes, e));
        setDirty(true);
    }, []);
    const onConnect = useCallback((params) => {
        setRfEdges((eds) => addEdge({
            ...params,
            markerEnd: { type: MarkerType.ArrowClosed, color: params.sourceHandle === "false" ? "#f87171" : (params.sourceHandle === "true" ? "#34d399" : "#8b5cf6") },
            style: { stroke: params.sourceHandle === "false" ? "#f87171" : (params.sourceHandle === "true" ? "#34d399" : "#8b5cf6"), strokeWidth: 2 },
            label: params.sourceHandle === "true" ? "true" : params.sourceHandle === "false" ? "false" : "",
            labelStyle: { fill: "#cbd5e1", fontSize: 10, fontWeight: 600 },
            labelBgStyle: { fill: "#0a0a1a" },
        }, eds));
        setDirty(true);
    }, []);

    const addNode = (type) => {
        const id = `${type.slice(0, 4)}_${Math.random().toString(36).slice(2, 8)}`;
        const defaults = {
            trigger: { label: "Manual" },
            workforce_action: { label: "New action", alias: id, worker_role: "", prompt: "" },
            condition: { label: "Branch", expression: "trigger.value > 0" },
            approval_gate: { label: "Approval", approver_role: "General Counsel", sla_hours: 24, message: "" },
            end: { marker: "success", summary: "" },
        }[type] || {};
        const newNode = {
            id, type: "atlas",
            position: { x: 220 + Math.random() * 220, y: 220 + Math.random() * 120 },
            data: { type, config: defaults },
        };
        setRfNodes((n) => [...n, newNode]);
        setSelected(newNode.id);
        setDirty(true);
    };

    const removeSelectedNode = () => {
        if (!selected) return;
        setRfNodes((n) => n.filter((x) => x.id !== selected));
        setRfEdges((e) => e.filter((x) => x.source !== selected && x.target !== selected));
        setSelected(null);
        setDirty(true);
    };

    const updateNodeConfig = (patch) => {
        if (!selected) return;
        setRfNodes((n) => n.map((x) => x.id === selected
            ? { ...x, data: { ...x.data, config: { ...(x.data.config || {}), ...patch } } }
            : x
        ));
        setDirty(true);
    };

    const selectedNode = rfNodes.find((n) => n.id === selected) || null;

    // -------- Save / validate / activate / run --------
    const payload = () => ({
        name, description,
        trigger: { type: triggerType, config: {} },
        nodes: orchNodesFromRf(rfNodes),
        edges: orchEdgesFromRf(rfEdges),
    });

    const doSave = async () => {
        setSaving(true);
        try {
            if (!currentId) {
                const { data } = await api.post("/orchestrations", payload());
                setCurrentId(data.orchestration.id);
                setStatus(data.orchestration.status);
                setDirty(false);
                toast.success("Draft saved.");
                nav(`/app/orchestrations/${data.orchestration.id}/edit`, { replace: true });
            } else {
                const { data } = await api.patch(`/orchestrations/${currentId}`, payload());
                setStatus(data.status);
                setDirty(false);
                toast.success("Saved.");
            }
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Save failed.");
        } finally { setSaving(false); }
    };

    const doValidate = async () => {
        if (dirty || !currentId) { await doSave(); }
        setValidating(true);
        try {
            const { data } = await api.post(`/orchestrations/${currentId}/validate`);
            setErrors(data.errors || []);
            if (data.valid) toast.success("Orchestration is valid.");
            else toast.error(`${(data.errors || []).length} validation issue(s).`);
        } catch (e) { toast.error("Validate failed."); }
        finally { setValidating(false); }
    };

    const doActivate = async () => {
        if (dirty || !currentId) { await doSave(); }
        setActivating(true);
        try {
            const { data } = await api.post(`/orchestrations/${currentId}/activate`);
            setStatus(data.status);
            setErrors([]);
            toast.success("Orchestration activated.");
        } catch (e) {
            const d = e.response?.data?.detail;
            if (d && typeof d === "object" && d.errors) {
                setErrors(d.errors);
                toast.error(`Fix ${d.errors.length} issue(s) to activate.`);
            } else {
                toast.error(typeof d === "string" ? d : "Activate failed.");
            }
        } finally { setActivating(false); }
    };

    const doRun = async () => {
        if (!currentId) return;
        setRunning(true);
        try {
            const { data } = await api.post(`/orchestrations/${currentId}/run`, { trigger_payload: {} });
            nav(`/app/orchestrations/${currentId}/runs/${data.run_id}`);
        } catch (e) {
            const d = e.response?.data?.detail;
            toast.error(typeof d === "string" ? d : "Run failed.");
        } finally { setRunning(false); }
    };

    const doDelete = async () => {
        if (!currentId) { nav("/app/orchestrations"); return; }
        setDeleting(true);
        try {
            await api.delete(`/orchestrations/${currentId}`);
            toast.success("Orchestration deleted.");
            nav("/app/orchestrations");
        } catch (e) { toast.error("Delete failed."); }
        finally { setDeleting(false); }
    };

    return (
        <div className="h-full flex flex-col" data-testid="orch-builder">
            {/* -------- TOP BAR -------- */}
            <div
                className="flex-shrink-0 px-6 h-14 flex items-center justify-between gap-3"
                style={{
                    background: "linear-gradient(180deg, rgba(10,10,26,0.95) 0%, rgba(5,5,16,0.95) 100%)",
                    borderBottom: "1px solid var(--atlas-border)",
                }}
            >
                <div className="flex items-center gap-3 min-w-0">
                    <Link to="/app/orchestrations"
                        className="text-[12px] inline-flex items-center gap-1"
                        style={{ color: "var(--atlas-text-tertiary)" }}
                        data-testid="orch-back">
                        <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2} /> Orchestrations
                    </Link>
                    <div className="w-px h-5" style={{ background: "var(--atlas-border)" }} />
                    <input
                        value={name}
                        onChange={(e) => { setName(e.target.value); setDirty(true); }}
                        placeholder="Orchestration name"
                        data-testid="orch-name-input"
                        className="text-[15px] font-semibold px-2 h-8 rounded-md bg-transparent"
                        style={{
                            color: "var(--atlas-text-primary)",
                            border: "1px solid transparent",
                            minWidth: 260,
                        }}
                        onFocus={(e) => e.currentTarget.style.borderColor = "var(--atlas-border-strong)"}
                        onBlur={(e) => e.currentTarget.style.borderColor = "transparent"}
                    />
                    <span
                        className="text-[9.5px] font-bold tracking-widest px-2 py-0.5 rounded-full"
                        style={
                            status === "active"
                                ? { background: "rgba(52,211,153,0.14)", border: "1px solid rgba(52,211,153,0.55)", color: "#34d399" }
                                : { background: "rgba(148,163,184,0.14)", border: "1px solid rgba(148,163,184,0.45)", color: "#cbd5e1" }
                        }
                        data-testid="orch-status-pill"
                    >
                        {status.toUpperCase()}
                    </span>
                    {dirty && (
                        <span className="text-[10px]" style={{ color: "#fbbf24" }}>Unsaved changes</span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={doSave} disabled={saving}
                        className="atlas-btn-ghost inline-flex items-center gap-1 rounded-lg px-3 h-9 text-[12.5px]"
                        data-testid="orch-save-draft-btn">
                        {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2}/> : <Save className="w-3.5 h-3.5" strokeWidth={2}/>}
                        Save draft
                    </button>
                    <button onClick={doValidate} disabled={validating}
                        className="atlas-btn-ghost inline-flex items-center gap-1 rounded-lg px-3 h-9 text-[12.5px]"
                        data-testid="orch-validate-btn">
                        {validating ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2}/> : <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2}/>}
                        Validate
                    </button>
                    <button onClick={doActivate} disabled={activating}
                        className="atlas-btn-primary inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-[12.5px]"
                        data-testid="orch-activate-btn">
                        {activating ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2}/> : <Sparkles className="w-3.5 h-3.5" strokeWidth={2}/>}
                        Activate
                    </button>
                    {status === "active" && (
                        <button onClick={doRun} disabled={running}
                            className="inline-flex items-center gap-1.5 rounded-lg px-3 h-9 text-[12.5px] font-medium"
                            style={{
                                background: "linear-gradient(135deg, #22d3ee 0%, #0891b2 100%)",
                                color: "#0a0a1a",
                                boxShadow: "0 0 0 1px rgba(34,211,238,0.55), 0 6px 18px -6px rgba(34,211,238,0.55)",
                            }}
                            data-testid="orch-run-btn">
                            {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={2}/> : <Play className="w-3.5 h-3.5" strokeWidth={2.5}/>}
                            Run
                        </button>
                    )}
                    <button onClick={() => setDeleteOpen(true)}
                        className="inline-flex items-center justify-center w-9 h-9 rounded-lg"
                        style={{ color: "#f87171", border: "1px solid rgba(239,68,68,0.35)" }}
                        data-testid="orch-delete-builder-btn">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                    </button>
                </div>
            </div>

            {/* -------- 3-COLUMN BODY -------- */}
            <div className="flex-1 min-h-0 flex overflow-hidden">
                {/* LEFT — palette */}
                <aside
                    className="w-[220px] flex-shrink-0 overflow-y-auto atlas-scrollbar p-4"
                    style={{
                        background: "linear-gradient(180deg, rgba(10,10,26,0.6) 0%, rgba(5,5,16,0.8) 100%)",
                        borderRight: "1px solid var(--atlas-border)",
                    }}
                    data-testid="orch-palette"
                >
                    <div className="atlas-label" style={{ color: "var(--atlas-text-muted)" }}>Node palette</div>
                    <div className="mt-3 space-y-1.5">
                        {PALETTE.map((p) => {
                            const Icon = p.icon;
                            return (
                                <button
                                    key={p.type}
                                    onClick={() => addNode(p.type)}
                                    data-testid={`orch-node-${p.type}`}
                                    className="w-full text-left px-3 py-2 rounded-lg flex items-center gap-2 transition-all"
                                    style={{
                                        background: "rgba(23,23,42,0.6)",
                                        border: "1px solid var(--atlas-border-strong)",
                                        color: "var(--atlas-text-secondary)",
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = p.color;
                                        e.currentTarget.style.background = `${p.color}14`;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = "var(--atlas-border-strong)";
                                        e.currentTarget.style.background = "rgba(23,23,42,0.6)";
                                    }}
                                >
                                    <Icon className="w-3.5 h-3.5" strokeWidth={2.25} style={{ color: p.color }} />
                                    <span className="text-[12.5px] font-medium">{p.label}</span>
                                    <Plus className="w-3 h-3 ml-auto" strokeWidth={2} style={{ color: "var(--atlas-text-muted)" }} />
                                </button>
                            );
                        })}
                    </div>

                    <div className="mt-6 atlas-label" style={{ color: "var(--atlas-text-muted)" }}>Trigger</div>
                    <select value={triggerType}
                        onChange={(e) => { setTriggerType(e.target.value); setDirty(true); }}
                        className="mt-2 w-full rounded-lg h-9 px-3 text-[12.5px]"
                        data-testid="orch-trigger-type">
                        <option value="manual">Manual</option>
                        <option value="webhook">Webhook</option>
                        <option value="scheduled">Scheduled (MOCKED)</option>
                        <option value="event">Event</option>
                    </select>

                    {errors.length > 0 && (
                        <div className="mt-6 rounded-lg p-3"
                            style={{ background: "var(--atlas-danger-muted)", border: "1px solid rgba(239,68,68,0.45)" }}
                            data-testid="orch-errors">
                            <div className="atlas-label" style={{ color: "#fca5a5" }}>Validation</div>
                            <ul className="mt-1 space-y-1 text-[11.5px]" style={{ color: "#fca5a5" }}>
                                {errors.map((er, i) => (
                                    <li key={i} className="flex items-start gap-1">
                                        <AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" strokeWidth={2}/>
                                        {er}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </aside>

                {/* CENTER — canvas */}
                <div className="flex-1 min-w-0 relative" style={{ background: "#050510" }}>
                    <ReactFlow
                        nodes={rfNodes}
                        edges={rfEdges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        nodeTypes={NODE_TYPES}
                        onNodeClick={(_, n) => setSelected(n.id)}
                        onPaneClick={() => setSelected(null)}
                        fitView
                        proOptions={{ hideAttribution: true }}
                    >
                        <Background variant="dots" gap={22} size={1} color="#1e1e34" />
                        <Controls
                            style={{ background: "#0a0a1a", border: "1px solid var(--atlas-border-strong)" }}
                            showInteractive={false}
                        />
                        <MiniMap
                            style={{ background: "#0a0a1a", border: "1px solid var(--atlas-border-strong)" }}
                            nodeColor={(n) => (NODE_META[n.data?.type]?.color) || "#8b5cf6"}
                            maskColor="rgba(0,0,0,0.5)"
                        />
                    </ReactFlow>
                </div>

                {/* RIGHT — inspector */}
                <aside
                    className="w-[340px] flex-shrink-0 overflow-y-auto atlas-scrollbar p-5"
                    style={{
                        background: "linear-gradient(180deg, rgba(10,10,26,0.55) 0%, rgba(5,5,16,0.85) 100%)",
                        borderLeft: "1px solid var(--atlas-border)",
                    }}
                    data-testid="orch-inspector-panel"
                >
                    {selectedNode ? (
                        <NodeInspector
                            node={selectedNode}
                            hired={hired}
                            allNodes={rfNodes}
                            onChange={updateNodeConfig}
                            onDelete={removeSelectedNode}
                        />
                    ) : (
                        <div>
                            <div className="atlas-label">Inspector</div>
                            <p className="mt-3 text-[12.5px]"
                                style={{ color: "var(--atlas-text-tertiary)" }}>
                                Click a node to edit its config. Drag from the right side of a node to draw a connection to the next step.
                            </p>
                            <div className="mt-5 atlas-label">Description</div>
                            <textarea
                                value={description}
                                onChange={(e) => { setDescription(e.target.value); setDirty(true); }}
                                rows={4}
                                placeholder="What does this orchestration do?"
                                className="w-full rounded-lg p-2 text-[12.5px]"
                                data-testid="orch-description"
                            />
                        </div>
                    )}
                </aside>
            </div>

            <ConfirmDeleteModal
                open={deleteOpen}
                title="Delete this orchestration?"
                body={`"${name}" and its draft edits will be deleted. Past run history is retained.`}
                confirmLabel="Delete orchestration"
                busy={deleting}
                onConfirm={doDelete}
                onClose={() => setDeleteOpen(false)}
            />
        </div>
    );
}

/* ============================================================
   Inspector — per-node config editor
   ============================================================ */
function NodeInspector({ node, hired, allNodes, onChange, onDelete }) {
    const meta = NODE_META[node.data.type];
    const cfg = node.data.config || {};
    const Icon = meta?.icon || Workflow;

    return (
        <div>
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-4 h-4" strokeWidth={2.25} style={{ color: meta?.color || "#8b5cf6" }} />
                    <div className="atlas-label truncate" style={{ color: meta?.color || "#8b5cf6" }}>
                        {meta?.label || node.data.type}
                    </div>
                </div>
                <button onClick={onDelete} title="Remove node"
                    className="inline-flex items-center justify-center w-7 h-7 rounded-md"
                    style={{ color: "#f87171", border: "1px solid rgba(239,68,68,0.35)" }}
                    data-testid="orch-node-delete">
                    <Trash2 className="w-3 h-3" strokeWidth={2} />
                </button>
            </div>

            <FormField label="Label">
                <input value={cfg.label || ""} onChange={(e) => onChange({ label: e.target.value })}
                    className="w-full rounded-md h-8 px-2 text-[12.5px]"
                    data-testid="orch-inspector-label" />
            </FormField>

            {node.data.type === "workforce_action" && (
                <>
                    <FormField label="Alias (used in {{variable}})" hint="Downstream nodes reference this as {{alias.output.text}}">
                        <input value={cfg.alias || ""} onChange={(e) => onChange({ alias: e.target.value })}
                            placeholder="e.g. draft"
                            className="w-full rounded-md h-8 px-2 text-[12.5px] font-mono"
                            data-testid="orch-inspector-alias" />
                    </FormField>
                    <FormField label="Workforce Member" hint="Pick a hired member or a role — role match is auto-resolved at run time.">
                        <select value={cfg.worker_slug || "__role__"}
                            onChange={(e) => onChange({ worker_slug: e.target.value === "__role__" ? "" : e.target.value })}
                            className="w-full rounded-md h-8 px-2 text-[12.5px]"
                            data-testid="orch-inspector-worker">
                            <option value="__role__">— Match by role —</option>
                            {(hired || []).map((h) => (
                                <option key={h.id} value={h.worker_slug}>
                                    {h.custom_name || h.worker_slug} ({h.worker_slug})
                                </option>
                            ))}
                        </select>
                    </FormField>
                    <FormField label="…or Workforce Member role" hint="Used only when no explicit member is selected.">
                        <input value={cfg.worker_role || ""} onChange={(e) => onChange({ worker_role: e.target.value })}
                            placeholder="e.g. Content Writer"
                            className="w-full rounded-md h-8 px-2 text-[12.5px]"
                            data-testid="orch-inspector-role" />
                    </FormField>
                    <FormField label="Prompt template" hint="Use {{trigger.field}} or {{alias.output.text}} to interpolate.">
                        <textarea value={cfg.prompt || ""} rows={6}
                            onChange={(e) => onChange({ prompt: e.target.value })}
                            className="w-full rounded-md p-2 text-[12.5px] font-mono resize-y"
                            data-testid="orch-inspector-prompt" />
                    </FormField>
                </>
            )}

            {node.data.type === "condition" && (
                <>
                    <FormField label="Expression" hint={"Ops: == != > < >= <= and or not contains startswith. Example: draft.output.text contains 'STRATEGIC'"}>
                        <textarea value={cfg.expression || ""} rows={3}
                            onChange={(e) => onChange({ expression: e.target.value })}
                            className="w-full rounded-md p-2 text-[12.5px] font-mono resize-y"
                            data-testid="orch-inspector-expression" />
                    </FormField>
                    <div className="mt-3 text-[10.5px]" style={{ color: "var(--atlas-text-muted)" }}>
                        Drag from the <span style={{ color: "#34d399" }}>green</span> handle for the <b>true</b> path,
                        and from the <span style={{ color: "#f87171" }}>red</span> handle for <b>false</b>.
                    </div>
                </>
            )}

            {node.data.type === "approval_gate" && (
                <>
                    <FormField label="Approver role">
                        <input value={cfg.approver_role || ""}
                            onChange={(e) => onChange({ approver_role: e.target.value })}
                            placeholder="e.g. General Counsel"
                            className="w-full rounded-md h-8 px-2 text-[12.5px]"
                            data-testid="orch-inspector-approver-role" />
                    </FormField>
                    <FormField label="Approver email (optional)">
                        <input value={cfg.approver_email || ""}
                            onChange={(e) => onChange({ approver_email: e.target.value })}
                            placeholder="approver@company.com"
                            className="w-full rounded-md h-8 px-2 text-[12.5px]"
                            data-testid="orch-inspector-approver-email" />
                    </FormField>
                    <FormField label="SLA (hours)">
                        <input type="number" min={1} value={cfg.sla_hours || 24}
                            onChange={(e) => onChange({ sla_hours: parseInt(e.target.value || "24", 10) })}
                            className="w-full rounded-md h-8 px-2 text-[12.5px]"
                            data-testid="orch-inspector-sla" />
                    </FormField>
                    <FormField label="Message to approver" hint="Supports {{variable}} interpolation.">
                        <textarea value={cfg.message || ""} rows={4}
                            onChange={(e) => onChange({ message: e.target.value })}
                            className="w-full rounded-md p-2 text-[12.5px] resize-y"
                            data-testid="orch-inspector-message" />
                    </FormField>
                </>
            )}

            {node.data.type === "trigger" && (
                <>
                    <FormField label="Trigger label">
                        <input value={cfg.label || ""} onChange={(e) => onChange({ label: e.target.value })}
                            className="w-full rounded-md h-8 px-2 text-[12.5px]" />
                    </FormField>
                    <div className="mt-3 text-[11.5px]" style={{ color: "var(--atlas-text-tertiary)" }}>
                        Trigger type is set on the left rail. Manual + webhook triggers execute immediately. Scheduled triggers are stored but not yet auto-fired (MOCKED SCHEDULER).
                    </div>
                </>
            )}

            {node.data.type === "end" && (
                <>
                    <FormField label="Marker">
                        <select value={cfg.marker || "success"} onChange={(e) => onChange({ marker: e.target.value })}
                            className="w-full rounded-md h-8 px-2 text-[12.5px]"
                            data-testid="orch-inspector-marker">
                            <option value="success">Success</option>
                            <option value="failure">Failure</option>
                        </select>
                    </FormField>
                    <FormField label="Summary" hint="Optional. Rendered into the run detail on completion.">
                        <textarea value={cfg.summary || ""} rows={3}
                            onChange={(e) => onChange({ summary: e.target.value })}
                            className="w-full rounded-md p-2 text-[12.5px] resize-y" />
                    </FormField>
                </>
            )}
        </div>
    );
}

function FormField({ label, hint, children }) {
    return (
        <div className="mt-4">
            <label className="block text-[11px] font-semibold uppercase tracking-widest mb-1"
                style={{ color: "var(--atlas-text-muted)" }}>
                {label}
            </label>
            {children}
            {hint && (
                <div className="mt-1 text-[10.5px] leading-relaxed"
                    style={{ color: "var(--atlas-text-muted)" }}>
                    {hint}
                </div>
            )}
        </div>
    );
}
